import { Music2 } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { Skeleton } from "@/components/ui/skeleton"
import { useFreezeHeightDuringPress } from "@/hooks/use-freeze-height-during-press"
import {
  rescanTiktokEmbeds,
  useTiktokEmbedScript,
} from "@/hooks/use-tiktok-embed-script"
import { cn } from "@/lib/utils"

/**
 * OKS-628 — Candidate TikTok **profile** embed (UX Spec §3 States 1–2).
 *
 * Renders the server-**sanitized** oEmbed blockquote and lets TikTok's
 * `embed.js` (loaded via {@link useTiktokEmbedScript}) hydrate it into the
 * profile card. Reuses the feed embed's visual + state contract (card
 * chrome `rounded-xl`/contained/centered/`max-w`, `Skeleton` shimmer,
 * `Music2` poster glyph, ~300ms cross-fade) but implements the
 * blockquote+script path fresh (TRD KD-1 — this is NOT the iframe player).
 *
 * **Safe render (security floor 90 — TRD KD-4):** the blockquote markup is
 * ALREADY server-sanitized by `Tiktok::ProfileOembed` (script + event
 * handlers + `javascript:`/`data:` hrefs stripped). We parse it with
 * `DOMParser` and adopt the resulting nodes — a parsed document NEVER
 * executes scripts, and we avoid React's raw-HTML escape hatch entirely.
 * embed.js mutates this subtree out of band (swaps the blockquote for an
 * `<iframe>`), so it lives in a ref'd node React does not reconcile. The
 * loader `<script>` is a separate real DOM element with a hardcoded src —
 * never injected from the payload.
 *
 * **Hydration detection:** a `MutationObserver` watches the container for
 * the injected `<iframe>` and cross-fades the card in. A settle timeout
 * reveals the raw blockquote (which still carries the profile link) if the
 * script never paints — so the viewer never sees an endless shimmer or a
 * broken frame (graceful degradation, same guarantee as the feed embed).
 *
 * **Layout (OKS-1007 / OKS-1015):** the container has a `min-h-[220px]` floor
 * (shared with the section skeleton and the fallback, so the deferred swap
 * moves nothing) and deliberately NO ceiling — a cap would crop the creator's
 * card (PO ruling). Its height therefore follows the blockquote, which is in
 * flow from mount, and later the injected `<iframe>`. That growth can land
 * mid-press on a control below; {@link useFreezeHeightDuringPress} pins the
 * OUTER container for the press (it is `overflow-hidden`, so growth inside the
 * inner node is clipped for the press and shown in full on release). Do not add
 * a React `style` prop to the container — it would drop the pin on re-render.
 * A shift that finishes BEFORE the press is ordinary layout shift and not
 * covered (BRIEF-OKS-1015, accepted gap).
 *
 * Locked testids: `tiktok-profile-embed` (container),
 * `tiktok-profile-embed-skeleton` (loading shimmer).
 */
interface TiktokProfileEmbedProps {
  /** Server-sanitized oEmbed blockquote markup. */
  html: string
  /** Candidate handle (no `@`) — accessible region name. */
  handle: string
  className?: string
}

/** Reveal the raw blockquote if `embed.js` hasn't painted within this window. */
const SETTLE_MS = 4000

/**
 * Parse sanitized markup into real DOM nodes and mount them under `node`.
 * `DOMParser` does not execute scripts, and the input is already
 * server-sanitized — no raw-HTML React escape hatch involved.
 */
function mountSanitized(node: HTMLElement, html: string): void {
  const parsed = new DOMParser().parseFromString(html, "text/html")
  const nodes = Array.from(parsed.body.childNodes).map((child) =>
    document.importNode(child, true),
  )
  node.replaceChildren(...nodes)
}

export function TiktokProfileEmbed({
  html,
  handle,
  className,
}: TiktokProfileEmbedProps) {
  const { t } = useTranslation()
  const rootRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [hydrated, setHydrated] = useState(false)
  const [settled, setSettled] = useState(false)

  // Load embed.js (idempotent, real <script> element) + re-scan on remount.
  useTiktokEmbedScript(true)

  // OKS-1015 — hold the container's height for any press outside it.
  useFreezeHeightDuringPress(rootRef)

  // Mount the sanitized blockquote once per `html`, ask an already-loaded
  // embed.js to hydrate it (SPA nav case — first load auto-scans), and
  // watch for the injected <iframe> that signals hydration completed.
  useEffect(() => {
    const node = containerRef.current
    if (!node) return

    mountSanitized(node, html)
    rescanTiktokEmbeds(node)

    const markHydrated = () => {
      if (node.querySelector("iframe")) {
        setHydrated(true)
        return true
      }
      return false
    }

    const observer = new MutationObserver(() => {
      if (markHydrated()) observer.disconnect()
    })
    observer.observe(node, { childList: true, subtree: true })

    const settleTimer = window.setTimeout(() => setSettled(true), SETTLE_MS)

    return () => {
      observer.disconnect()
      window.clearTimeout(settleTimer)
    }
  }, [html])

  const revealed = hydrated || settled

  return (
    <div
      ref={rootRef}
      data-testid="tiktok-profile-embed"
      data-hydrated={hydrated}
      role="group"
      aria-label={t("candidates.detail.tiktok_profile.region_aria", {
        handle,
      })}
      className={cn(
        "bg-muted/40 relative mx-auto min-h-[220px] w-full max-w-[420px] overflow-hidden rounded-xl",
        className,
      )}
    >
      {/* Decorative poster glyph behind the blockquote (UX Spec §3 S2). */}
      {!hydrated && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 flex items-center justify-center"
        >
          <Music2 className="text-muted-foreground size-10 opacity-40" />
        </div>
      )}

      {/* Loading shimmer until hydrate or settle. */}
      {!revealed && (
        <Skeleton
          data-testid="tiktok-profile-embed-skeleton"
          aria-hidden="true"
          className="absolute inset-0 rounded-xl"
        />
      )}

      {/* Sanitized blockquote — mounted via DOMParser (see effect). */}
      <div
        ref={containerRef}
        className={cn(
          "relative transition-opacity duration-300",
          revealed ? "opacity-100" : "opacity-0",
        )}
      />
    </div>
  )
}
